import { call } from '@everlutionsk/helpers';
import { Pagination } from '@everlutionsk/ui';
import { ErrorOutline } from '@mui/icons-material';
import {
  Box,
  CheckboxProps,
  Checkbox as MuiCheckbox,
  Pagination as MuiPagination,
  Table as MuiTable,
  TableContainerProps as MuiTableContainerProps,
  TableProps as MuiTableProps,
  Skeleton,
  Stack,
  SxProps,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableRowProps,
  TableSortLabel,
  Typography,
  styled
} from '@mui/material';
import React, { ReactNode, memo, useRef } from 'react';
import { useLatestNonNilValue } from '../coreComponents/useLatestNonNilValue';
import { NoResultFound } from '../noResultFound';

type PaginationPosition = 'top' | 'bottom' | 'both';

export interface TableTheme {
  defaultProps: {
    readonly paginationPosition?: PaginationPosition;
  };
}

export interface TableProps<TItem = any, TSort = any> {
  readonly items?: readonly TItem[];
  readonly rowKey?: RowKeyGenerator<TItem>;
  readonly rowProps?: (item: TItem, index: number) => TableRowProps;
  readonly columns: ReadonlyArray<TableColumn<TItem, TSort> | false | undefined | null>;
  readonly pagination?: Pagination;
  readonly bottomToolbar?: ReactNode;
  readonly loading?: boolean;
  readonly error?: Error | Error[];
  readonly sort?: TableSortConfig<TSort>;
  readonly muiTableBoxWrapperSxOverrides?: SxProps;
  readonly muiTableProps?: MuiTableProps;
  readonly muiTableContainerProps?: MuiTableContainerProps;
  readonly placeholderRows?: number;
  readonly noDataPlaceholder?: ReactNode;
  readonly topToolbar?: ReactNode;
  readonly paginationPosition?: PaginationPosition;
}

export interface TableSortConfig<T = any> {
  readonly current: T;
  readonly onChange: (value: T | undefined) => void;
}

interface ItemsWithoutId<TItem> extends TableProps<TItem> {
  readonly rowKey: RowKeyGenerator<TItem>;
}

type ItemsWithId<TItem extends { id: string }> = TableProps<TItem>;

export interface TableColumn<TItem, TSort> {
  readonly label: ReactNode;
  readonly labelCheckbox?: CheckboxProps;
  readonly render: ((item: TItem, index: number) => ReactNode) | keyof TItem;
  readonly itemCheckbox?: (item: TItem) => CheckboxProps;
  readonly align?: TableColumnAlign;
  readonly sort?: ColumnSort<TSort>;
}

interface ColumnSort<T> {
  readonly asc: T;
  readonly desc: T;
  readonly default: T | undefined;
}

export type TableColumnAlign = 'left' | 'right' | 'center';
type RowKeyGenerator<TItem> = (item: TItem, index: number) => string | number;

/**
 * Basic table.
 *
 * `rowKey` generator is optional if items includes `id` property.
 */
export function Table<TItem>(props: ItemsWithoutId<TItem>): JSX.Element;
export function Table<TItem extends { id: string }>(props: ItemsWithId<TItem>): JSX.Element;
export function Table({
  items,
  rowKey,
  rowProps = () => ({}),
  columns: rawColumns,
  pagination,
  bottomToolbar,
  error,
  loading = items == null && error == null,
  sort: tableSort,
  muiTableProps,
  muiTableBoxWrapperSxOverrides,
  muiTableContainerProps,
  placeholderRows,
  noDataPlaceholder,
  topToolbar,
  paginationPosition
}: TableProps) {
  const pagingPosition = paginationPosition ?? 'bottom';

  const latestItems = useLatestNonNilValue(items);
  const latestPagination = useRef<Pagination | undefined>(pagination);

  // Caches pagination between loads to avoid jumping to 0-0 page on query variables change.
  if (!loading) {
    latestPagination.current = pagination;
  }

  const latestItemsWereEmpty = latestItems == null || latestItems?.length === 0;

  const showRowsPlaceholder = items == null && latestItemsWereEmpty && loading;
  const showNoDataPlaceholder = items?.length === 0;

  const columns = rawColumns.filter(isColumn);

  const isPaginationNeeded = call((): boolean => {
    if (latestPagination.current == null) return false;

    return latestPagination.current.count > latestPagination.current.rowsPerPage;
  });

  const paginationSx: SxProps = {
    visibility: latestItemsWereEmpty ? 'hidden' : 'visible',
    display: showNoDataPlaceholder ? 'none' : 'block',
    margin: 0,
    padding: 0
  };

  const topBoth: string[] = ['top, both'];
  const bottomBoth: string[] = ['bottom', 'both'];

  return (
    <>
      <TableContainer
        {...muiTableContainerProps}
        sx={{
          backgroundColor: theme => theme.brandPalette.background.faded,
          borderRadius: theme => theme.radius['3xl'],
          overflowX: 'auto'
        }}
      >
        {topToolbar != null && (
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box sx={{ mb: latestPagination.current == null ? '12px' : undefined }}>
              {topToolbar}
            </Box>

            {latestPagination.current != null &&
              topBoth.includes(pagingPosition) &&
              isPaginationNeeded && (
                <TablePagination component="div" sx={paginationSx} {...latestPagination.current} />
              )}
          </Stack>
        )}

        {!topBoth.includes(pagingPosition) && topToolbar != null && <Box my={1}>{topToolbar}</Box>}

        {latestPagination.current != null &&
          topToolbar == null &&
          topBoth.includes(pagingPosition) &&
          isPaginationNeeded && (
            <TablePagination component="div" sx={paginationSx} {...latestPagination.current} />
          )}

        <Box
          sx={
            muiTableBoxWrapperSxOverrides ?? {
              width: '100%',
              display: 'table',
              tableLayout: 'fixed'
            }
          }
        >
          <MuiTable
            {...muiTableProps}
            sx={{ borderColor: theme => theme.brandPalette.background.faded, overflow: 'visible' }}
          >
            <TableHead>
              <TableRow>
                {columns.map(({ label, align, sort: columnSort, labelCheckbox }, index) => {
                  function toSortDirection() {
                    if (tableSort?.current == null) return;
                    if (tableSort.current === columnSort?.asc) return 'asc';
                    if (tableSort.current === columnSort?.desc) return 'desc';
                  }

                  const sortDirection = toSortDirection();

                  return (
                    <Cell
                      align={align ?? 'left'}
                      key={index}
                      sortDirection={sortDirection}
                      sx={{
                        color: theme => theme.brandPalette.foreground.neutralFaded,
                        fontWeight: '530'
                      }}
                    >
                      {labelCheckbox && (
                        <Checkbox
                          {...labelCheckbox}
                          disabled={showNoDataPlaceholder ? true : labelCheckbox.disabled}
                        />
                      )}
                      {tableSort && columnSort ? (
                        <TableSortLabel
                          active={sortDirection != null}
                          direction={sortDirection}
                          onClick={() => {
                            if (sortDirection == null) {
                              tableSort.onChange(columnSort.asc);
                            } else if (sortDirection === 'asc') {
                              tableSort.onChange(columnSort.desc);
                            } else if (sortDirection === 'desc') {
                              if (columnSort.desc !== columnSort.default) {
                                tableSort.onChange(columnSort.default);
                              } else {
                                tableSort.onChange(columnSort.asc);
                              }
                            }
                          }}
                        >
                          {label}
                        </TableSortLabel>
                      ) : (
                        <Typography variant="labelSmall" sx={{ fontWeight: 500 }}>
                          {label}
                        </Typography>
                      )}
                    </Cell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRows
                items={items ?? latestItems}
                columns={columns}
                rowKey={rowKey}
                rowProps={rowProps}
              />
              {showRowsPlaceholder && (
                <RowsPlaceholder
                  rows={placeholderRows ?? latestPagination.current?.rowsPerPage ?? 4}
                  columns={columns.length}
                />
              )}
              {showNoDataPlaceholder && (
                <NoDataPlaceholder columns={columns.length} noDataPlaceholder={noDataPlaceholder} />
              )}
              {error && <ErrorPlaceholder error={error} columns={columns.length} />}
            </TableBody>
          </MuiTable>
        </Box>
      </TableContainer>
      {latestPagination.current != null &&
        bottomToolbar != null &&
        bottomBoth.includes(pagingPosition) &&
        isPaginationNeeded && (
          <Box display="flex" justifyContent="space-between" sx={{ pt: 3, pb: 1 }}>
            <Box my={1} mx={2}>
              {bottomToolbar}
            </Box>
            <MuiPagination sx={paginationSx} {...latestPagination.current} />
          </Box>
        )}
      {latestPagination.current == null && bottomToolbar != null && (
        <Box my={1} mx={2}>
          {bottomToolbar}
        </Box>
      )}
      {latestPagination.current != null &&
        bottomToolbar == null &&
        bottomBoth.includes(pagingPosition) &&
        isPaginationNeeded && (
          <Box sx={{ pt: 3, pb: 1 }}>
            <MuiPagination
              sx={paginationSx}
              count={Math.ceil(
                latestPagination.current.count / latestPagination.current.rowsPerPage
              )}
              page={latestPagination.current.page + 1}
              onChange={(e, page) => {
                if (latestPagination.current == null) return;
                latestPagination.current.onPageChange(e as any, page - 1);
              }}
            />
          </Box>
        )}
    </>
  );
}

function TableRows<TItem = any, TSort = any>({
  items,
  columns,
  rowKey: generateRowKey = item => (item as any).id,
  rowProps
}: {
  items: readonly TItem[] | undefined;
  columns: Array<TableColumn<TItem, TSort>>;
  rowKey: RowKeyGenerator<TItem> | undefined;
  rowProps: (item: TItem, index: number) => TableRowProps;
}) {
  if (!items) return null;

  return (
    <>
      {items.map((item, rowIndex) => {
        const rowKey = generateRowKey(item, rowIndex);
        return (
          <TableRow key={rowKey} hover {...rowProps(item, rowIndex)}>
            {columns.map((column, columnIndex) => (
              <Cell key={`${rowKey}.${columnIndex}`}>
                <CellData
                  sx={{
                    justifyContent: column.align ?? 'left',
                    color: theme => theme.brandPalette.foreground.neutral
                  }}
                >
                  {column.itemCheckbox && <Checkbox {...column.itemCheckbox(item)} />}
                  {formatValue(
                    typeof column.render === 'function'
                      ? column.render(item, rowIndex)
                      : item[column.render]
                  )}
                </CellData>
              </Cell>
            ))}
          </TableRow>
        );
      })}
    </>
  );
}

function isColumn<T>(value: T | null | undefined | false): value is T {
  return value != null && value !== false;
}

function formatValue(value: any) {
  if (typeof value === 'number') return value.toLocaleString();
  return value;
}

const RowsPlaceholder = memo(({ rows, columns }: { rows: number; columns: number }) => {
  return (
    <>
      {Array.from(Array(rows)).map((_, index) => (
        <TableRow key={index}>
          <Cell colSpan={columns}>
            <Skeleton />
          </Cell>
        </TableRow>
      ))}
    </>
  );
});

const NoDataPlaceholder = memo(
  ({
    columns,
    noDataPlaceholder
  }: {
    columns: number;
    noDataPlaceholder: ReactNode | undefined;
  }) => {
    return (
      <TableRow>
        <Cell colSpan={columns} align="left">
          <Box sx={{ color: 'text.disabled' }} component="span">
            {noDataPlaceholder ?? <NoResultFound />}
          </Box>
        </Cell>
      </TableRow>
    );
  }
);

const ErrorPlaceholder = memo(({ error, columns }: { error: Error | Error[]; columns: number }) => {
  const errors = Array.isArray(error) ? error : [error];
  return (
    <TableRow>
      <Cell colSpan={columns} align="center">
        <ErrorIcon />
        {errors.map((error, index) => (
          <ErrorMessage key={index}>{error.message}</ErrorMessage>
        ))}
      </Cell>
    </TableRow>
  );
});

const ErrorIcon = styled(ErrorOutline)(({ theme }) => ({
  paddingTop: '10px',
  color: theme.palette.error.main,
  width: '36px',
  height: '36px'
}));

const ErrorMessage = styled('span')(({ theme }) => ({
  display: 'block',
  color: theme.palette.error.main,
  paddingBottom: '10px'
}));

const Cell = styled(TableCell)`
  min-width: 90px;
  max-width: 400px;
  white-space: break-spaces;
  a {
    white-space: break-spaces;
    display: inline-block;
    @media (max-width: 960px) {
      min-width: 120px;
    }
  }
`;

const CellData = styled(Box)({
  display: 'inline-flex',
  width: '100%',
  alignItems: 'center'
});

const Checkbox = styled(MuiCheckbox)(({ theme }) => ({
  padding: theme.spacing(0.5),
  marginRight: theme.spacing(0.5)
}));
